/**
 * Main menu — a TITLE SCREEN, not a web ui (user rule): the menu is its
 * own page (opaque, with a track illustration; the game never renders
 * behind it). Native buttons and a form support touch, Tab and Enter.
 *
 * Primary choices: racer count and a free-text SEED (any text; blank
 * mints a random one). A folded "Simulation options" section carries
 * classic loop, physics, autopilot law, debug overlay and asset gallery.
 * Boost speed and fuel efficiency
 * are NOT here — they are pilot personality traits (autopilot.ts
 * pilotFor), not user settings.
 *
 * Routing contract: the menu owns a BARE url (no search, no hash) and
 * ?menu=1; every shared seed link and harness url (hash and/or params
 * present) boots straight into the game, so shared URLs and the CUA
 * soaks keep working. START always writes an explicit `endless` param,
 * so the post-start url is never bare. Choices persist in localStorage
 * as prefill for the next bare visit.
 */

/** Settings the menu writes into the url (read by main.ts / endless.ts). */
export interface MenuChoices {
  endless: boolean;
  peers: number;
  physics: boolean;
  autopilot: boolean;
  debug: boolean;
  gallery: boolean;
}

export const MENU_DEFAULTS: MenuChoices = {
  endless: true,
  peers: 5,
  physics: true,
  autopilot: true,
  debug: false,
  gallery: false,
};

const STORE_KEY = "marbles-menu";

function loadStored(): Partial<MenuChoices> {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) ?? "{}");
  } catch {
    return {};
  }
}

/** Prefill: url params (a ?menu=1 revisit keeps the session's settings)
 * override stored choices override defaults. */
function prefill(params: URLSearchParams): MenuChoices {
  const stored = { ...MENU_DEFAULTS, ...loadStored() };
  const num = (key: string, fallback: number): number => {
    const v = Number.parseFloat(params.get(key) ?? "");
    return Number.isFinite(v) ? v : fallback;
  };
  return {
    endless: params.has("endless")
      ? params.get("endless") !== "0"
      : stored.endless,
    peers: Math.max(0, Math.min(9998, Math.round(num("peers", stored.peers)))),
    physics: params.has("phys") ? params.get("phys") !== "0" : stored.physics,
    autopilot: params.has("autopilot")
      ? params.get("autopilot") !== "0"
      : stored.autopilot,
    debug: params.has("debug") ? params.get("debug") === "1" : stored.debug,
    gallery: params.has("gallery")
      ? params.get("gallery") === "1"
      : stored.gallery,
  };
}

function urlFor(c: MenuChoices, seed: string): string {
  const q = new URLSearchParams();
  // Always explicit — a bare url means "show the menu".
  q.set("endless", c.endless ? "1" : "0");
  if (c.endless) {
    if (c.peers !== MENU_DEFAULTS.peers) q.set("peers", String(c.peers));
    if (!c.physics) q.set("phys", "0");
    if (!c.autopilot) q.set("autopilot", "0");
  }
  if (c.debug) q.set("debug", "1");
  if (c.gallery) q.set("gallery", "1");
  const hash = seed === "" ? "" : `#${encodeURIComponent(seed)}`;
  return `${location.pathname}?${q.toString()}${hash}`;
}

/** Current seed from the hash (returning via ?menu=1 keeps it). */
function currentSeed(): string {
  try {
    return decodeURIComponent(location.hash.slice(1));
  } catch {
    return location.hash.slice(1);
  }
}

/** A full-screen title menu. Shared seed URLs still boot directly into play. */
export function maybeShowMenu(): boolean {
  const params = new URLSearchParams(location.search);
  const bare = location.search === "" && location.hash === "";
  if (!bare && params.get("menu") !== "1") return false;

  const c = prefill(params);
  const root = document.createElement("main");
  root.id = "menu";
  root.innerHTML = `
    <div class="menu-top"><a class="wordmark" href="/" aria-label="Marbles home"><i aria-hidden="true"></i></a></div>
    <div class="menu-layout">
      <div class="menu-art" aria-hidden="true">
        <div class="art-orbit orbit-one"></div><div class="art-orbit orbit-two"></div>
        <svg class="track-art" viewBox="0 0 600 560" fill="none">
          <defs>
            <linearGradient id="track-ink" x1="80" y1="60" x2="520" y2="480" gradientUnits="userSpaceOnUse"><stop stop-color="#8ee3cf"/><stop offset="1" stop-color="#284b60"/></linearGradient>
            <radialGradient id="orb-orange" cx=".3" cy=".25" r=".8"><stop stop-color="#fff2d3"/><stop offset=".25" stop-color="#ffc088"/><stop offset=".6" stop-color="#f07953"/><stop offset="1" stop-color="#7c3044"/></radialGradient>
            <radialGradient id="orb-blue" cx=".3" cy=".25" r=".8"><stop stop-color="#ecfffc"/><stop offset=".3" stop-color="#a0e9df"/><stop offset=".65" stop-color="#62a9ba"/><stop offset="1" stop-color="#2a435f"/></radialGradient>
            <radialGradient id="orb-lilac" cx=".3" cy=".25" r=".8"><stop stop-color="#fff4ff"/><stop offset=".3" stop-color="#c6b6ee"/><stop offset=".65" stop-color="#9583c9"/><stop offset="1" stop-color="#493757"/></radialGradient>
            <filter id="art-shadow" x="-30%" y="-30%" width="160%" height="180%"><feDropShadow dx="0" dy="18" stdDeviation="12" flood-color="#020b13" flood-opacity=".5"/></filter>
          </defs>
          <g filter="url(#art-shadow)" transform="rotate(-17 300 280)">
            <path d="M135 125H390Q475 125 475 210V260Q475 340 395 340H210Q130 340 130 415Q130 470 205 470H340" stroke="#0b1e2d" stroke-width="60" stroke-linecap="round"/>
            <path d="M135 113H390Q475 113 475 198V248Q475 328 395 328H210Q130 328 130 403Q130 458 205 458H340" stroke="url(#track-ink)" stroke-width="53" stroke-linecap="round"/>
            <path d="M135 113H390Q475 113 475 198V248Q475 328 395 328H210Q130 328 130 403Q130 458 205 458H340" stroke="#193b4a" stroke-width="35" stroke-linecap="round"/>
            <path d="M135 111H390Q475 111 475 196V246Q475 326 395 326H210Q130 326 130 401Q130 456 205 456H340" stroke="#315764" stroke-width="24" stroke-linecap="round"/>
            <path d="M154 94H385" stroke="#bdfff0" stroke-opacity=".5" stroke-width="2" stroke-linecap="round"/>
            <path d="M455 209V244Q455 308 397 308" stroke="#bdfff0" stroke-opacity=".35" stroke-width="2" stroke-linecap="round"/>
            <circle cx="272" cy="95" r="36" fill="url(#orb-orange)"/>
            <ellipse cx="262" cy="83" rx="12" ry="7" transform="rotate(-30 262 83)" fill="white" fill-opacity=".65"/>
            <circle cx="450" cy="277" r="24" fill="url(#orb-blue)"/>
            <ellipse cx="443" cy="269" rx="7" ry="4" transform="rotate(-30 443 269)" fill="white" fill-opacity=".65"/>
            <circle cx="220" cy="311" r="20" fill="url(#orb-lilac)"/>
            <circle cx="215" cy="304" r="5" fill="white" fill-opacity=".6"/>
          </g>
          <circle cx="100" cy="250" r="3" fill="#92dcca"/><circle cx="470" cy="440" r="2" fill="#f3b798"/>
          <path d="M490 76v14m-7-7h14M91 431v10m-5-5h10" stroke="#b3cacd" stroke-opacity=".6"/>
        </svg>
      </div>
      <form class="menu-panel" id="menu-form">
        <h1>marbles<span>.</span></h1>
        <fieldset class="field-choices endless-only"><legend>How many racers?</legend>
          <div class="racer-options">
            <button type="button" data-racers="1">Solo</button><button type="button" data-racers="6">6</button>
            <button type="button" data-racers="32">32</button><button type="button" data-racers="128">128</button>
            <input id="menu-racers" placeholder="Custom" type="number" min="1" max="9999" step="1" inputmode="numeric" aria-label="Custom racer count" title="Custom racer count, 1 to 9999">
          </div>
        </fieldset>
        <div class="seed-field"><label for="menu-seed">World seed <span>optional</span></label>
          <input id="menu-seed" placeholder="Surprise me" spellcheck="false" autocomplete="off">
        </div>
        <button type="submit" id="start-run" class="start-run">Let's roll <span aria-hidden="true">↗</span></button>
        <details class="menu-advanced"><summary>Simulation options</summary><div class="adv">
          <label class="endless-only steering-setting">Steering <select id="menu-ap"><option value="modern">Modern autopilot</option><option value="legacy">Legacy track assist</option></select></label>
          <label><input type="checkbox" id="menu-classic"> Classic loop</label>
          <label class="endless-only"><input type="checkbox" id="menu-phys"> Physical simulation</label>
          <label><input type="checkbox" id="menu-debug"> Track diagnostics</label>
          <label><input type="checkbox" id="menu-gallery"> Piece gallery</label>
        </div></details>
      </form>
    </div>
  `;
  document.body.appendChild(root);
  const el = <T extends HTMLElement>(id: string): T =>
    root.querySelector(`#${id}`) as T;
  const seedInput = el<HTMLInputElement>("menu-seed");
  const racersInput = el<HTMLInputElement>("menu-racers");
  const classicInput = el<HTMLInputElement>("menu-classic");
  seedInput.value = currentSeed();
  if (!c.endless) {
    root.querySelector<HTMLDetailsElement>(".menu-advanced")!.open = true;
  }
  const sync = () => {
    classicInput.checked = !c.endless;
    if (document.activeElement !== racersInput) {
      racersInput.value = [1, 6, 32, 128].includes(c.peers + 1)
        ? ""
        : String(c.peers + 1);
    }
    for (
      const button of root.querySelectorAll<HTMLButtonElement>("[data-racers]")
    ) {
      button.setAttribute(
        "aria-pressed",
        String(Number(button.dataset.racers) === c.peers + 1),
      );
    }
    for (const row of root.querySelectorAll<HTMLElement>(".endless-only")) {
      row.classList.toggle("off", !c.endless);
      for (
        const control of row.querySelectorAll<
          HTMLInputElement | HTMLButtonElement | HTMLSelectElement
        >("input, button, select")
      ) control.disabled = !c.endless;
    }
    (window as unknown as Record<string, unknown>).render_game_to_text = () =>
      JSON.stringify({ mode: "menu", choices: c, seed: seedInput.value });
  };
  classicInput.addEventListener("change", () => {
    c.endless = !classicInput.checked;
    sync();
  });
  for (
    const button of root.querySelectorAll<HTMLButtonElement>("[data-racers]")
  ) {
    button.addEventListener("click", () => {
      c.peers = Number(button.dataset.racers) - 1;
      sync();
    });
  }
  racersInput.addEventListener("input", () => {
    const count = racersInput.valueAsNumber;
    if (Number.isFinite(count)) {
      c.peers = Math.max(0, Math.min(9998, Math.round(count) - 1));
    }
    sync();
  });
  racersInput.addEventListener("blur", () => {
    sync();
  });
  const steeringSelect = el<HTMLSelectElement>("menu-ap");
  steeringSelect.value = c.autopilot ? "modern" : "legacy";
  steeringSelect.addEventListener("change", () => {
    c.autopilot = steeringSelect.value === "modern";
  });
  for (
    const [id, key] of [["menu-phys", "physics"], [
      "menu-debug",
      "debug",
    ], ["menu-gallery", "gallery"]] as const
  ) {
    const input = el<HTMLInputElement>(id);
    input.checked = c[key];
    input.addEventListener("change", () => {
      c[key] = input.checked;
    });
  }
  const start = () => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(c));
    } catch { /* URL carries the choices when storage is unavailable. */ }
    location.replace(urlFor(c, seedInput.value));
  };
  el<HTMLFormElement>("menu-form").addEventListener("submit", (e) => {
    e.preventDefault();
    start();
  });
  addEventListener("keydown", (e) => {
    const target = e.target as HTMLElement | null;
    if (
      target?.closest("input, button, summary, textarea, select") ||
      e.isComposing
    ) return;
    if (e.key === "Enter") {
      e.preventDefault();
      start();
    }
  });
  sync();
  return true;
}
